import { axiosInstance, axiosJsonInstance } from "@/axios.instance";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { SummaryData } from "@/store/site-monitor/types";
import AlarmTagsPagedResponse from "@/types/sv-data/alarms/AlarmTagsPagedResponse";
import { EventNotification } from "@/types/sv-data/notification-rule/EventNotification";
import { EventAlarmTagRequest } from "@/types/sv-data/tags/EventAlarmTag";
import { EventAlarmTagPagedResponse } from "@/types/sv-data/tags/EventAlarmTagPagedResponse";
import { HttpStatusCode } from "@/utils/HttpStatusCode";
import { AxiosInstance } from "axios";
import { RetrievedEventLinks } from "@/store/event-links/types";

class EventsApi {
	private axios: AxiosInstance;
	private axiosJson: AxiosInstance;

	constructor() {
		this.axios = axiosInstance;
		this.axiosJson = axiosJsonInstance;
	}

	public async getAlarmTagsForEvent(eventId: number, paginatedSearchRequest?: PaginatedSearchRequest): Promise<EventAlarmTagPagedResponse> {
		const { data } = await this.axios.get(`/Event/AlarmTagsForEvent/${eventId}`, paginatedSearchRequest);
		return data;
	}

	public async getEventAlarmTagsForArea(paginatedSearchRequest?: PaginatedSearchRequest): Promise<AlarmTagsPagedResponse> {
		const { data } = await this.axios.get(`/Event/EventAlarmTagsForArea`, paginatedSearchRequest);
		return data;
	}

	public async removeEventAlarmTag(request: EventAlarmTagRequest): Promise<void> {
		await this.axiosJson.put(`/Event/RemoveEventAlarmTag`, request);
	}

	public async addEventAlarmTag(request: EventAlarmTagRequest): Promise<void> {
		await this.axiosJson.post(`/Event/AddEventAlarmTag`, request);
	}

	public async updateEventQueueItem (eventId: number, eventText: string | null, priority: number | null, overWriteText = false): Promise<void> {
		await this.axios.put(`/Event/UpdateEventQueueItem?` +
			`eventId=${eventId}` +
			(eventText ? `&eventText=${encodeURIComponent(eventText)}` : "") +
			(priority !== null ? `&priority=${priority}` : "") +
			(overWriteText ? `&overwriteText=${overWriteText}` : ""));
	}

	public async preserveEventOutcome(eventId: number, eventOutcomeId: number | null, eventOutcomeNote: string | null): Promise<void> {
		await this.axiosJson.put(`/Event/PreserveEventOutcome?` +
			`eventId=${eventId}` +
			(eventOutcomeId === null ? "" : `&eventOutcomeId=${eventOutcomeId}`) +
			(eventOutcomeNote === null ? "" : `&eventOutcomeNote=${encodeURIComponent(eventOutcomeNote)}`));
	}

	public async getSentEventNotifications(eventId: number): Promise<EventNotification[]> {
		const { data, status } = await this.axiosJson.get(`Event/GetSentEventNotifications?eventId=${eventId}`);
		if (status == HttpStatusCode.NoContent){
			return [];
		}
		return data;
	}

	public async getSavedEventNotifications(eventId: number): Promise<EventNotification[] | null> {
		const { data, status } = await this.axiosJson.get(`Event/${eventId}/SavedNotifications`);
		if (status != HttpStatusCode.Ok){
			return [];
		}

		return data;
	}

	public async getEventSummaryData(eventId: number, apiKey: string): Promise<SummaryData> {
		const { data } = await this.axios.get(`Event/ExecutiveSummaryDataByApiKey/${eventId}`, {
			headers: {
				Authorization: apiKey
			}
		});
        return data;
	}

	public async createEventLink(baseEventId: number, selectedEventId: number): Promise<unknown> {
		const { data } = await this.axios.post(`Event/CreateEventLink?` + `eventId1=${baseEventId}&` + `eventId2=${selectedEventId}`);
		return data;
	}

	public async retrieveEventLinks(request: PaginatedSearchRequest): Promise<RetrievedEventLinks> {
		const { data } = await this.axios.get<RetrievedEventLinks>(`Event/GetEventLinks`, { params: request.params });
		return data;
	}

	public async deleteEventLink(eventId1: number, eventId2: number): Promise<unknown> {
		const { data } = await this.axios.delete(`Event/DeleteEventLink?` + `eventId1=${eventId1}&` + `eventId2=${eventId2}`);
		return data;
	}
}

const eventsApi = new EventsApi();
export default eventsApi;
